import { useMemo, useDeferredValue, MutableRefObject } from 'react'
import { ITableRowInGroup, GroupByDictionary, IGroupRowCreate, IGroupCollapsed, ITreeGroupRow } from '../types'
import { ITableRow, ITableViewColumn, ITableColumn, ITableViewGroup, ICellValue } from 'types'
import {
  sortRows,
  isGroupRowCreate,
  isCollapsedGroupRow,
  isTreeGroupRow
} from 'components/spreadsheet/helpers/functions'
import { rowGrouper, GROUP_KEY_PREFIX, NO_VALUE_KEY } from 'components/spreadsheet/helpers/grouping'
import { GROUP_KEY_SPLIT } from '../constants/const'
import { clampScrollTop, getMaxScrollTop } from 'components/spreadsheet/helpers/scroll'
import { SMALL_ROW_HEIGHT } from 'style/constants'
import { CompressedColumnStore } from 'components/spreadsheet/helpers/compressed-column-store'

// The spread operator (and Array.assign) loads the entire source array
// onto the stack, then pushes it onto the destination array.  Once the
// source array exceeds 125,052, it causes a Stack Overflow / Range Error.
const MAX_STACK_SIZE = 10000

// Empty array singletons to avoid creating new arrays when unmounting
const EMPTY_ROWS: ITableRow[] = []
const EMPTY_EXPANDED_ROWS: Array<ITableRowInGroup | IGroupRowCreate | IGroupCollapsed | ITreeGroupRow | ITableRow> = []

interface Args {
  gridHeight: number
  rowHeight: number
  // The grid's logical vertical scroll offset (see helpers/scroll.ts); clamped here.
  scrollTop: number
  rawRows: ITableRow[]
  groupBy: ITableViewGroup[]
  expandedGroupIds?: ReadonlySet<unknown>
  totalRows: number
  collapsedGroupIds: Set<string>
  collapseAllGroupsPending: boolean
  isLoading: boolean
  columns: ITableViewColumn[]
  isContributor: boolean
  disableNewRow: boolean
  unpackMultiselectGroupView: boolean
  columnIdToIndex: Map<string, number>
  indexToColumnId: string[]
  columnStore?: CompressedColumnStore | null
  treeGroupView: boolean
  isUnmountedRef: MutableRefObject<boolean>
  stickyContentHeight: number
  visibleBitmap?: Uint8Array | null
  visibleCount?: number
}

const unpackRows = (
  rows: ITableRow[],
  columnId: string,
  columnIdToIndex: Map<string, number>,
  indexToColumnId: string[],
  columnStore?: CompressedColumnStore | null
): ITableRow[] => {
  const newRows: ITableRow[] = []
  const columnIndex = columnIdToIndex.get(columnId)
  if (columnIndex === undefined) return rows

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    // Build rowData from the store if not present on the row
    let rowData: ICellValue[]
    if (row.rowData) {
      rowData = row.rowData
    } else if (columnStore && row._rowIndex >= 0) {
      rowData = indexToColumnId.map((_, idx) => columnStore.get(row._rowIndex, idx))
    } else {
      rowData = []
    }

    const multiselectValue = rowData[columnIndex]
    if (multiselectValue && Array.isArray(multiselectValue)) {
      for (let j = 0; j < multiselectValue.length; j++) {
        const value = multiselectValue[j]
        if (typeof value === 'string') {
          const newRowData = [...rowData]
          newRowData[columnIndex] = [value]
          newRows.push({ ...row, rowData: newRowData, _rowIndex: -1 })
        }
      }
    }
    if (!multiselectValue) {
      const newRowData = [...rowData]
      newRowData[columnIndex] = []
      newRows.push({ ...row, rowData: newRowData, _rowIndex: -1 })
    }
  }
  return newRows
}

export function useRowsViewport({
  gridHeight,
  rowHeight,
  scrollTop,
  rawRows,
  groupBy,
  totalRows,
  collapsedGroupIds,
  collapseAllGroupsPending,
  isLoading,
  columns,
  isContributor,
  disableNewRow,
  unpackMultiselectGroupView,
  treeGroupView,
  columnIdToIndex,
  indexToColumnId,
  columnStore,
  isUnmountedRef,
  stickyContentHeight,
  visibleBitmap,
  visibleCount: visibleCountProp
}: Args) {
  'use no memo'
  const shouldGroup = groupBy.length > 0 && !isLoading

  // Effective visible row count: use bitmap popcount when filtering is active,
  // otherwise fall back to totalRows (all rows are visible).
  const effectiveVisibleCount = visibleBitmap ? (visibleCountProp ?? totalRows) : totalRows

  // Defer rawRows for the grouping path so the heavy sort+group computation runs
  // at low priority. The row delete is reflected immediately in state, and the
  // re-grouping catches up without blocking the UI.
  const deferredRawRows = useDeferredValue(rawRows)

  // eslint-disable-next-line
  const [groupedRows, _] = useMemo(() => {
    // Return empty when unmounting to release memory
    if (isUnmountedRef.current) return [undefined, 0]
    // If we're not grouping
    if (!shouldGroup) return [undefined, totalRows]

    // Check if need to unpack multiselect columns
    const multiselectColumnIds = columns
      .filter((c) => c.kind === 'multiselect' && groupBy.find((group) => group.columnId === c.publicId))
      .map((c) => c.publicId)
    // When a bitmap is active, only group visible rows
    let rowsToGroup: ITableRow[] = deferredRawRows
    if (visibleBitmap) {
      rowsToGroup = []
      for (let i = 0; i < deferredRawRows.length; i++) {
        if (visibleBitmap[i]) rowsToGroup.push(deferredRawRows[i])
      }
    }
    if (unpackMultiselectGroupView) {
      let newRows: ITableRow[] = []
      for (let i = 0; i < rowsToGroup.length; i++) {
        const row = rowsToGroup[i]
        let unpackedRows = [row]
        for (let j = 0; j < multiselectColumnIds.length; j++) {
          const columnId = multiselectColumnIds[j]
          const newUnpackedRows = unpackRows(unpackedRows, columnId, columnIdToIndex, indexToColumnId, columnStore)
          unpackedRows = newUnpackedRows
        }
        newRows = newRows.concat(...unpackedRows)
      }
      rowsToGroup = newRows.map((row, index) => ({ ...row, sortOrder: index }))
    }
    const groupRows = (
      rows: ITableRow[],
      groupByColumns: ITableColumn[],
      startRowIndex: number
    ): [GroupByDictionary, number] => {
      if (rows.length === 0) return [{}, 0]
      if (groupByColumns.length === 0) return [{}, 0]

      const walk = (
        levelRows: ITableRow[],
        depth: number,
        rowIndex: number
      ): [GroupByDictionary | ITableRow[], number] => {
        // Leaf: no more columns, return raw rows and their count
        if (depth >= groupByColumns.length) return [levelRows, levelRows.length]

        const col = groupByColumns[depth]
        let groupRowsCount = 0
        const groups: GroupByDictionary = {}

        for (const [key, childRows] of Object.entries(rowGrouper(levelRows, col, columnIdToIndex, columnStore))) {
          const [childGroups, childCount] = walk(childRows, depth + 1, rowIndex + groupRowsCount + 1)
          groups[key] = {
            childRows,
            childGroups,
            startRowIndex: rowIndex + groupRowsCount,
            groupByColumn: col
          }
          groupRowsCount += childCount + 1 // +1 for this group row
        }

        return [groups, groupRowsCount]
      }

      const [groups, count] = walk(rows, 0, startRowIndex)
      // At the top level we know it’s a dictionary (not raw rows) unless there were zero columns;
      // handle that case if needed.
      return [Array.isArray(groups) ? {} : groups, count]
    }

    // We need to sort the data by the columns we're grouping by, then by
    // any columns specified in the user's sort
    const sortBy = groupBy
    const sortedRows = sortRows(rowsToGroup, sortBy, columns, columnIdToIndex, columnStore)

    // Get columns used for grouping (filter out any that no longer exist)
    const toGroupColumns = groupBy
      .map((toGroupColumn) => columns.find((c) => c.publicId === toGroupColumn.columnId))
      .filter((c): c is ITableViewColumn => c !== undefined)

    return groupRows(sortedRows, toGroupColumns, 0)
  }, [groupBy, rowGrouper, deferredRawRows, shouldGroup, columns, columnIdToIndex, columnStore, visibleBitmap])

  type ExpandedRow = ITableRowInGroup | IGroupRowCreate | IGroupCollapsed | ITreeGroupRow | ITableRow
  const [expandedRowsWithGroups] = useMemo((): [ExpandedRow[]] => {
    // Return empty when unmounting to release memory
    if (isUnmountedRef.current) return [EMPTY_EXPANDED_ROWS]
    if (!groupedRows) return [[]]

    // --- Tree group view: emit ITreeGroupRow nodes + plain ITableRow leaves ---
    if (treeGroupView) {
      const flattenedRows: Array<ITreeGroupRow | ITableRow | IGroupRowCreate> = []
      const emptyKey = `${GROUP_KEY_PREFIX}${NO_VALUE_KEY}`

      // A subtree has a "renderable descendant" if, walking it top-down, we find any
      // leaf whose groupKey is non-empty, OR any non-empty group at any level. Empty
      // leaves are always hidden (at depth > 0), and empty non-leaves are hidden when
      // their own subtree has no renderable descendant — so the decision cascades up
      // the tree. Results are cached per subtree: each empty bucket is checked twice
      // (once for reorder, once for hide-decision) and nested empty chains would
      // otherwise re-scan the same descendants at every depth.
      const renderableDescendantCache = new WeakMap<GroupByDictionary, boolean>()
      const hasRenderableDescendant = (subtree: GroupByDictionary | ITableRow[]): boolean => {
        if (Array.isArray(subtree)) return false
        const cached = renderableDescendantCache.get(subtree)
        if (cached !== undefined) return cached
        let result = false
        for (const key of Object.keys(subtree)) {
          const child = subtree[key]
          const isEmptyKey = key === emptyKey
          const isLeaf = Array.isArray(child.childGroups)
          if (!isEmptyKey) {
            result = true
            break
          }
          if (isLeaf) continue
          if (hasRenderableDescendant(child.childGroups)) {
            result = true
            break
          }
        }
        renderableDescendantCache.set(subtree, result)
        return result
      }

      const expandTreeGroup = (
        group: GroupByDictionary | ITableRow[],
        parentKeys: string[],
        depth: number,
        // visibleDepth tracks the depth of the nearest *visible* ancestor + 1, so
        // rows promoted out of hidden (Empty) groups still indent under the deepest
        // shown header. It only increments when a header is actually emitted.
        visibleDepth: number
      ) => {
        if (Array.isArray(group)) {
          // Leaf rows — push them with the visible-ancestor depth attached so the
          // tree-leaf cell can apply the matching paddingLeft.
          for (let i = 0; i < group.length; i++) {
            const row = group[i]
            const wrapped = Object.create(row) as ITableRow
            wrapped.treeDepth = visibleDepth
            flattenedRows.push(wrapped)
          }
          // Add-row after leaf group
          if (isContributor && !disableNewRow && group.length > 0) {
            flattenedRows.push({
              isAddRow: true,
              lastGroupRow: group[group.length - 1]
            })
          }
          return
        }

        // When the (Empty) bucket at this level will be hidden, process it before
        // its siblings so its promoted rows appear above the next non-empty header
        // (matching the column-grouped view order).
        const keys = Object.keys(group)
        if (depth > 0) {
          const emptyIdx = keys.indexOf(emptyKey)
          if (emptyIdx > 0) {
            const g = group[emptyKey]
            if (Array.isArray(g.childGroups) || !hasRenderableDescendant(g.childGroups)) {
              keys.unshift(...keys.splice(emptyIdx, 1))
            }
          }
        }

        for (const groupKey of keys) {
          const groupKeyPath = [...parentKeys, groupKey]
          const id = groupKeyPath.join(GROUP_KEY_SPLIT)
          const childGroup = group[groupKey]
          const isExpanded = !collapseAllGroupsPending && !collapsedGroupIds.has(id)

          // Hide (Empty) bucket *headers* to reduce visual noise when rows haven't
          // been fully classified — but still emit the rows beneath them so the data
          // stays visible under the nearest non-hidden ancestor. The first grouping
          // column is excluded (single-column grouped views still show their (Empty)
          // bucket). At deeper levels: empty leaves always hide their header, and
          // empty non-leaves hide their header when their subtree contains no
          // renderable descendant — so chains of (Empty) groups cascade away.
          if (depth > 0 && groupKey === emptyKey) {
            const isLeaf = Array.isArray(childGroup.childGroups)
            if (isLeaf || !hasRenderableDescendant(childGroup.childGroups)) {
              // Always emit descendants — the header is hidden so the user has no
              // way to toggle it, and we'd otherwise lose the rows entirely. Keep
              // visibleDepth unchanged so rows align under the last *visible* header.
              expandTreeGroup(childGroup.childGroups, groupKeyPath, depth + 1, visibleDepth)
              continue
            }
          }

          // Strip the GROUP_KEY_PREFIX from the display label
          const label = groupKey.startsWith(GROUP_KEY_PREFIX) ? groupKey.slice(GROUP_KEY_PREFIX.length) : groupKey

          const allGroupKeys: string[] = []
          for (let i = 0; i < groupKeyPath.length; i++) {
            allGroupKeys.push(groupKeyPath.slice(0, i + 1).join(GROUP_KEY_SPLIT))
          }

          const treeGroupRow: ITreeGroupRow = {
            isTreeGroupRow: true,
            depth,
            label: label === 'EmptyCellValue' ? '(Empty)' : label,
            groupKey: id,
            groupKeys: allGroupKeys,
            isExpanded,
            childRows: childGroup.childRows,
            groupByColumn: childGroup.groupByColumn
          }
          flattenedRows.push(treeGroupRow)

          if (isExpanded) {
            expandTreeGroup(childGroup.childGroups, groupKeyPath, depth + 1, visibleDepth + 1)
          }
        }
      }

      expandTreeGroup(groupedRows, [], 0, 0)
      return [flattenedRows]
    }

    // --- Standard (column-based) group view ---
    const flattenedRows: Array<ITableRowInGroup | IGroupRowCreate | IGroupCollapsed> = []

    // While we're expanding the grouped rows (GroupByDictionary) into an array of rows
    // we track which group keys we've seen. This is used to only render the names of
    // groups in the first row of the group
    const seenGroupKeys = new Set<string>()

    // Lightweight wrapper: uses Object.create so the row's properties are
    // inherited via prototype chain instead of being copied. Only the 4 group
    // metadata fields live on the new object, avoiding 200k+ object spreads.
    const wrapRow = (row: ITableRow, groupKeys: string[]): ITableRowInGroup => {
      const wrapped = Object.create(row) as ITableRowInGroup
      wrapped.isGrouped = true
      wrapped.groupsToRender = []
      wrapped.groupKeys = groupKeys
      wrapped.start = false
      return wrapped
    }

    const expandGroup = (group: GroupByDictionary | ITableRow[], parentKeys: string[]) => {
      if (Array.isArray(group)) {
        if (group.length === 0) return

        // Since a group can be nested inside other groups, we track the current group key
        // and all parent groups this row is a part of
        const groupKeys: string[] = []
        for (let i = 0; i < parentKeys.length; i++) {
          groupKeys.push(parentKeys.slice(0, i + 1).join(GROUP_KEY_SPLIT))
        }

        const firstRow = wrapRow(group[0], groupKeys)
        firstRow.start = true

        // groupsToRender determines whether the value for the GroupCell should be rendered:
        // only on the first row of each group the user hasn't been shown yet.
        for (const key of groupKeys) {
          if (!seenGroupKeys.has(key)) {
            seenGroupKeys.add(key)
            firstRow.groupsToRender.push(key)
          }
        }

        flattenedRows.push(firstRow)

        // Wrap remaining rows without copying all their properties
        const otherCount = group.length - 1
        if (otherCount > MAX_STACK_SIZE) {
          for (let i = 1; i < group.length; i++) {
            flattenedRows.push(wrapRow(group[i], groupKeys))
          }
        } else if (otherCount > 0) {
          for (let i = 1; i < group.length; i++) {
            flattenedRows.push(wrapRow(group[i], groupKeys))
          }
        }

        // The row representing the create row component for the group
        const lastRow: ITableRow = group.length > 1 ? (flattenedRows[flattenedRows.length - 1] as ITableRow) : firstRow
        const createNewGroupRow: IGroupRowCreate = {
          isAddRow: true,
          lastGroupRow: lastRow
        }

        // Only show if user is permissioned to add rows to table
        if (isContributor && !disableNewRow) {
          flattenedRows.push(createNewGroupRow)
        }

        return
      }

      for (const groupKey of Object.keys(group)) {
        const groupKeyPath = [...parentKeys, groupKey]
        const id = groupKeyPath.join(GROUP_KEY_SPLIT)
        const childGroup = group[groupKey]
        const groups = []

        // If the group has been collapsed, we insert a IGroupCollapsed
        // row into the list of rows
        if (collapseAllGroupsPending || collapsedGroupIds.has(id)) {
          const groupsToRender: string[] = []
          for (let i = 0; i < groupKeyPath.length; i++) {
            const key = groupKeyPath.slice(0, i + 1).join(GROUP_KEY_SPLIT)
            if (!seenGroupKeys.has(key)) {
              seenGroupKeys.add(key)
              groupsToRender.push(key)
            }
            groups.push(key)
          }

          const collapsedGroupRow = {
            isCollapsedRow: true,
            groupKey: id,
            groupKeys: groups,
            row: childGroup.childRows[0],
            groupsToRender
          }
          flattenedRows.push(collapsedGroupRow)
          continue
        }

        expandGroup(childGroup.childGroups, [...parentKeys, groupKey])
      }
    }

    expandGroup(groupedRows, [])

    return [flattenedRows]
  }, [groupedRows, collapsedGroupIds, collapseAllGroupsPending, disableNewRow, isContributor, treeGroupView])

  // Precompute a prefix-sum of visible rows for O(log n) "Nth visible row" lookup.
  // prefixSum[i] = number of visible rows in rawRows[0..i-1].
  // When no bitmap is active (all visible), this is null and we use direct indexing.
  const visiblePrefixSum = useMemo(() => {
    if (!visibleBitmap || shouldGroup) return null
    const sum = new Uint32Array(visibleBitmap.length + 1)
    for (let i = 0; i < visibleBitmap.length; i++) {
      sum[i + 1] = sum[i] + visibleBitmap[i]
    }
    return sum
  }, [visibleBitmap, shouldGroup])

  // Precompute cumulative heights for grouped rows so we can binary-search for the
  // correct viewport indices. Without this, the uniform-height formula
  // `floor(logicalScrollTop / rowHeight)` increasingly misses the visible rows as
  // the cumulative offset from shorter add-rows (SMALL_ROW_HEIGHT) grows.
  const groupedCumulativeHeights = useMemo(() => {
    if (!shouldGroup || expandedRowsWithGroups.length === 0) return null
    const len = expandedRowsWithGroups.length
    const heights = new Float64Array(len + 1)
    for (let i = 0; i < len; i++) {
      const r = expandedRowsWithGroups[i]
      const h = isGroupRowCreate(r) || isCollapsedGroupRow(r) || isTreeGroupRow(r) ? SMALL_ROW_HEIGHT : rowHeight
      heights[i + 1] = heights[i] + h
    }
    return heights
  }, [shouldGroup, expandedRowsWithGroups, rowHeight])

  // Calculate the viewport window from the logical scroll offset. Vertical scrolling is virtual
  // (see helpers/scroll.ts): the offset is clamped to the rows' total height less the rows area.
  const [logicalTotalHeight, startRowIndex, endRowIndex, logicalScrollTop, maxScrollTop] = useMemo(() => {
    // Return empty when unmounting to release memory
    if (isUnmountedRef.current) return [0, 0, 0, 0, 0]
    // Extra rows added before and after the actual visible rows
    const rowPadding = 5
    const rowsAreaHeight = Math.max(0, gridHeight - stickyContentHeight)
    // For standard display of rows
    if (!shouldGroup) {
      const addRowHeight = disableNewRow ? 0 : SMALL_ROW_HEIGHT
      const logicalTotalHeight = effectiveVisibleCount * rowHeight + addRowHeight
      const maxScrollTop = getMaxScrollTop(logicalTotalHeight, rowsAreaHeight)
      const logicalScrollTop = clampScrollTop(scrollTop, maxScrollTop)

      const startRowIndex = Math.max(
        0,
        Math.min(effectiveVisibleCount - 30, Math.floor(logicalScrollTop / rowHeight) - rowPadding)
      )
      const endRowIndex = Math.min(
        effectiveVisibleCount,
        Math.floor((logicalScrollTop + gridHeight) / rowHeight + rowPadding)
      )

      return [logicalTotalHeight, startRowIndex, endRowIndex, logicalScrollTop, maxScrollTop]

      // When we're grouping rows — use cumulative heights for correct index lookup
    } else {
      const totalExpandedRows = expandedRowsWithGroups.length
      let logicalTotalHeight: number
      if (groupedCumulativeHeights) {
        logicalTotalHeight = groupedCumulativeHeights[totalExpandedRows]
      } else {
        let smallRowCount = 0
        for (let i = 0; i < totalExpandedRows; i++) {
          const r = expandedRowsWithGroups[i]
          if (isGroupRowCreate(r) || isCollapsedGroupRow(r) || isTreeGroupRow(r)) smallRowCount++
        }
        logicalTotalHeight = (totalExpandedRows - smallRowCount) * rowHeight + smallRowCount * SMALL_ROW_HEIGHT
      }

      const maxScrollTop = getMaxScrollTop(logicalTotalHeight, rowsAreaHeight)
      const logicalScrollTop = clampScrollTop(scrollTop, maxScrollTop)

      // Binary search: find the first row whose cumulative bottom edge exceeds the target position.
      // This correctly handles mixed row heights (data rows at rowHeight, add-rows at SMALL_ROW_HEIGHT).
      if (groupedCumulativeHeights) {
        const heights = groupedCumulativeHeights

        // Find row at logicalScrollTop
        let lo = 0
        let hi = totalExpandedRows
        while (lo < hi) {
          const mid = (lo + hi) >>> 1
          if (heights[mid + 1] <= logicalScrollTop) lo = mid + 1
          else hi = mid
        }
        const startRowIndex = Math.max(0, lo - rowPadding)

        // Find row at logicalScrollTop + gridHeight
        lo = startRowIndex
        hi = totalExpandedRows
        while (lo < hi) {
          const mid = (lo + hi) >>> 1
          if (heights[mid + 1] <= logicalScrollTop + gridHeight) lo = mid + 1
          else hi = mid
        }
        const endRowIndex = Math.min(totalExpandedRows, lo + rowPadding)

        return [logicalTotalHeight, startRowIndex, endRowIndex, logicalScrollTop, maxScrollTop]
      }

      // Fallback if cumulative heights aren't available (shouldn't happen)
      const startRowIndex = Math.max(
        0,
        Math.min(totalExpandedRows - 30, Math.floor(logicalScrollTop / rowHeight) - rowPadding)
      )
      const endRowIndex = Math.min(
        totalExpandedRows,
        Math.floor((logicalScrollTop + gridHeight) / rowHeight + rowPadding)
      )

      return [logicalTotalHeight, startRowIndex, endRowIndex, logicalScrollTop, maxScrollTop]
    }
  }, [
    shouldGroup,
    expandedRowsWithGroups.length,
    groupedCumulativeHeights,
    effectiveVisibleCount,
    gridHeight,
    scrollTop,
    rowHeight,
    disableNewRow,
    stickyContentHeight
  ])

  // Slice the rows array only when indices or source data changes.
  // When a bitmap is active, startRowIndex/endRowIndex refer to the Nth visible row,
  // so we use the prefix-sum to map back to raw array indices.
  const rows = useMemo(() => {
    // Return empty when unmounting to release memory
    if (isUnmountedRef.current) return EMPTY_ROWS
    if (!shouldGroup) {
      if (visiblePrefixSum && visibleBitmap) {
        // Binary search to find the raw index of the Nth visible row
        const findRawIndex = (visibleN: number): number => {
          let lo = 0
          let hi = visiblePrefixSum.length - 1
          while (lo < hi) {
            const mid = (lo + hi) >>> 1
            if (visiblePrefixSum[mid + 1] <= visibleN) lo = mid + 1
            else hi = mid
          }
          return lo
        }

        const rawStart = startRowIndex > 0 ? findRawIndex(startRowIndex) : 0
        const count = endRowIndex - startRowIndex
        const result: ITableRow[] = []
        for (let i = rawStart; i < rawRows.length && result.length < count; i++) {
          if (visibleBitmap[i]) {
            result.push(rawRows[i])
          }
        }
        return result
      }
      return rawRows.slice(startRowIndex, endRowIndex)
    } else {
      return expandedRowsWithGroups.slice(startRowIndex, endRowIndex)
    }
  }, [shouldGroup, rawRows, expandedRowsWithGroups, startRowIndex, endRowIndex, visiblePrefixSum, visibleBitmap])

  // Count short rows (add-rows and group header rows) before the viewport so Rows can
  // initialise addRowOffset correctly. Without this, rows near the bottom are positioned
  // too far down because the positioning formula (rowNumber * rowHeight) assumes full-height rows.
  const preViewportAddRowCount = useMemo(() => {
    if (!shouldGroup || startRowIndex === 0) return 0
    let count = 0
    for (let i = 0; i < startRowIndex; i++) {
      const r = expandedRowsWithGroups[i]
      if (isGroupRowCreate(r) || isCollapsedGroupRow(r) || isTreeGroupRow(r)) count++
    }
    return count
  }, [shouldGroup, expandedRowsWithGroups, startRowIndex])

  // Return empty values when unmounting to ensure all references are released
  if (isUnmountedRef.current) {
    return {
      rowsCount: 0,
      rows: EMPTY_ROWS,
      startRowIndex: 0,
      endRowIndex: 0,
      logicalTotalHeight: 0,
      logicalScrollTop: 0,
      maxScrollTop: 0,
      groupedRows: undefined,
      expandedRowsWithGroups: EMPTY_EXPANDED_ROWS,
      preViewportAddRowCount: 0,
      groupedCumulativeHeights: null
    }
  }

  return {
    rowsCount: expandedRowsWithGroups.length,
    rows,
    startRowIndex,
    endRowIndex,
    logicalTotalHeight,
    logicalScrollTop,
    maxScrollTop,
    groupedRows,
    expandedRowsWithGroups,
    preViewportAddRowCount,
    groupedCumulativeHeights
  }
}
